import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import {
  DIARY_TIME_ZONE_OPTIONS,
  defaultDiaryZoneValue,
  storedOffsetForZone,
  wallClockFromCentral,
  windowsZoneId,
} from '../../../functions/todo/diary-notification-time';
import { mapDiaryUserOptions } from '../../../functions/todo/diary-user-format';
import { rememberDiarySearchUpdate } from '../../../functions/todo/search-retain-updates';
import { FormatPipe } from '../../../pipes/format.pipe';
import { ClientDiaryDataService } from '../../../services/todo/client-diary-data.service';
import {
  DIARY_PREF_ALLOW_STATUS_UPDATE,
  DIARY_PREF_TYPE,
  readDiaryYesNoPreference,
} from './diary-storage.keys';
import { ListItem } from '../../../models/api/core/list/list-item';
import { focusFirstInvalid } from '../../../utilities/focus-first-invalid';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { DatePickerComponent } from '../../core/controls/date-picker.component';
import { SelectComponent } from '../../core/controls/select.component';
import { TextareaComponent } from '../../core/controls/textarea.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../core/labels/required-fields-note.component';
import { ToastComponent } from '../../core/labels/toast.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-diary-edit-page',
  imports: [
    LoadingPanelComponent,
    EmptyStateComponent,
    ButtonComponent,
    DatePickerComponent,
    SelectComponent,
    CheckboxComponent,
    TextareaComponent,
    ActionRowComponent,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    RequiredFieldsNoteComponent,
    ToastComponent,
  ],
  template: `
    <app-page-header
      [label]="headerLabel"
      [backRouterLink]="backRouterLink"
      [backLabel]="backLabel"
    />
    <app-loading-panel
      [state]="state"
      [error]="error"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
    >
      <ng-template #empty>
        <app-empty-state icon="fa-history" label="Empty history" height="450px">
          <span>TODO empty state</span>
        </app-empty-state>
      </ng-template>

      <ng-template #loaded>
        <form [formGroup]="fg" #formEl>
          <app-required-fields-note />
          <div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 lg:tw-gap-0">
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-1/3 tw-min-w-0 tw-gap-2 lg:tw-gap-0"
            >
              <app-select
                [options]="clientOptions"
                optionLabel="description"
                optionValue="value"
                class="tw-basis-1/3"
                [virtualScroll]="true"
                [control]="fg.controls.client"
                label="Client"
                [multiple]="false"
                [disabled]="editMode || isClaimSpecific || isDiaryTiedToClaim"
              />
              <app-select
                [options]="nameOptions"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.followupUser"
                label="Follow-up user"
                class="tw-w-full"
                [multiple]="false"
              />
              <app-select
                [options]="additionalUsersOptions"
                optionLabel="description"
                optionValue="value"
                [control]="fg.controls.selectedUsers"
                label="Additional follow-up users"
                class="tw-w-full"
                [multiple]="true"
              />
              <app-date-picker
                class="tw-w-full"
                [control]="fg.controls.dueDate"
                label="Due date"
                [helpText]="dueDateHelpText"
              />
              <app-select
                [options]="diaryTypes"
                optionLabel="description"
                optionValue="value"
                class="tw-w-full"
                [virtualScroll]="false"
                [control]="fg.controls.diaryType"
                label="Diary type"
                [multiple]="false"
              />
              @if (allowComplete) {
                <app-checkbox
                  label="Allow other users to update the diary status"
                  [control]="fg.controls.allowOthers"
                />
              }
              <app-checkbox
                label="Send email reminder"
                [control]="fg.controls.sendEmail"
              />
              @if (fg.controls.sendEmail.value) {
                <div
                  class="tw-flex tw-flex-col tw-gap-2"
                  role="group"
                  aria-label="Notification time"
                >
                  <div class="tw-font-bold tw-text-[var(--sed-black-80-color)]">
                    Notification time
                  </div>
                  <div
                    class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2"
                  >
                    <app-date-picker
                      class="tw-w-full"
                      [control]="fg.controls.notificationTime"
                      label="Time"
                      [timeOnly]="true"
                    />
                    <app-select
                      [options]="timezoneOptions"
                      optionLabel="description"
                      optionValue="value"
                      [control]="fg.controls.notifyTimezone"
                      label="Time zone"
                      [multiple]="false"
                    />
                  </div>
                  <app-toast type="Info">
                    Please note – the full diary text will be transmitted in the
                    email notification on the due date.
                  </app-toast>
                </div>
              }
              @if (headerLabel !== 'New diary' && allowComplete) {
                <app-checkbox
                  label="Completed"
                  [control]="fg.controls.completed"
                />
              }
            </div>
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-2/3 tw-min-w-0 lg:tw-pl-4"
            >
              <app-textarea
                [control]="fg.controls.defaultText"
                label="Text"
                rows="16"
              ></app-textarea>
              @if (editMode) {
                <div
                  class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-pt-4"
                  role="group"
                  aria-label="Diary creation details"
                >
                  <div>
                    <div
                      class="tw-font-bold tw-text-[var(--sed-black-80-color)] tw-pb-1"
                      id="diary-created-by-label"
                    >
                      Created by
                    </div>
                    <div
                      class="tw-text-[var(--sed-dynamic-black-color)]"
                      aria-labelledby="diary-created-by-label"
                    >
                      {{ createdByDisplay }}
                    </div>
                  </div>
                  <div>
                    <div
                      class="tw-font-bold tw-text-[var(--sed-black-80-color)] tw-pb-1"
                      id="diary-created-on-label"
                    >
                      Created on
                    </div>
                    <div
                      class="tw-text-[var(--sed-dynamic-black-color)]"
                      aria-labelledby="diary-created-on-label"
                    >
                      {{ createdOnDisplay }}
                    </div>
                  </div>
                </div>
                @if (showUpdatedFields) {
                  <div
                    class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-pt-4"
                    role="group"
                    aria-label="Diary update details"
                  >
                    <div>
                      <div
                        class="tw-font-bold tw-text-[var(--sed-black-80-color)] tw-pb-1"
                        id="diary-updated-by-label"
                      >
                        Updated by
                      </div>
                      <div
                        class="tw-text-[var(--sed-dynamic-black-color)]"
                        aria-labelledby="diary-updated-by-label"
                      >
                        {{ updatedByDisplay }}
                      </div>
                    </div>
                    <div>
                      <div
                        class="tw-font-bold tw-text-[var(--sed-black-80-color)] tw-pb-1"
                        id="diary-updated-on-label"
                      >
                        Updated on
                      </div>
                      <div
                        class="tw-text-[var(--sed-dynamic-black-color)]"
                        aria-labelledby="diary-updated-on-label"
                      >
                        {{ updatedOnDisplay }}
                      </div>
                    </div>
                  </div>
                }
              }
            </div>
          </div>
          <app-action-row useMobileLayout>
            <app-button
              right1
              type="submit"
              [loading]="loading"
              label="Save Diary"
              (click)="onSaveDiary()"
            />
            <app-button
              right2
              emphasis="Low"
              [routerLink]="backRouterLink"
              label="Cancel"
            ></app-button>
          </app-action-row>
        </form>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
  providers: [FormatPipe],
})
export class DiaryEditPageComponent extends BaseComponent implements OnInit {
  private clientDiaryData = inject(ClientDiaryDataService);
  private formatPipe = inject(FormatPipe);

  @ViewChild('formEl', { read: ElementRef })
  formEl!: ElementRef<HTMLElement>;

  @Output()
  search = new EventEmitter();

  @Output()
  clear = new EventEmitter();

  @Input()
  loading = false;

  editMode = false;
  isPreloading = false;
  currentDiaryId: number | null = null;
  isClaimSpecific = false;
  isDiaryTiedToClaim = false;

  backRouterLink = '/todo';

  backLabel = 'My to-do';

  headerLabel = 'New diary';

  allowComplete = true;

  dueDateHelpText = '';

  createdByDisplay = '';

  createdOnDisplay = '';

  updatedByDisplay = '';

  updatedOnDisplay = '';

  showUpdatedFields = false;

  private generalInfoGeneration = 0;

  private dueDateHelpGeneration = 0;

  timezoneOptions = DIARY_TIME_ZONE_OPTIONS.map((zone) => ({
    description: zone.description,
    value: zone.value,
  }));

  protected state: LoadingState = 'Loading';
  error: any = null;

  fg = new FormGroup({
    client: new FormControl<any>(null, [Validators.required]),
    followupUser: new FormControl<string>(null, [Validators.required]),
    dueDate: new FormControl<Date>(null, Validators.required),
    diaryType: new FormControl<ListItem>(null, Validators.required),
    allowOthers: new FormControl<boolean>(true),
    sendEmail: new FormControl<boolean>(false),
    notificationTime: new FormControl<Date>(null),
    notifyTimezone: new FormControl<number>(6),
    defaultText: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(10000),
    ]),
    tempUser: new FormControl<string>(null),
    selectedUsers: new FormControl<string[]>(null),
    completed: new FormControl<boolean>(false),
  });

  clientOptions = [{ description: '---', value: 'x' }];

  diaryTypes = [{ description: '---', value: 'x' }];

  nameOptions = [{ description: '---', value: 'x' }];

  /** Options for Additional follow-up users, excluding the selected Follow-up user (same user cannot be in both). */
  get additionalUsersOptions(): ListItem[] {
    const followUp = this.fg?.controls?.followupUser?.value;
    if (followUp == null || followUp === '') return this.nameOptions;
    return this.nameOptions.filter((o) => String(o.value) !== String(followUp));
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit() {
    const data = this.storage.get('current-diary');
    this.backRouterLink = data?.backRouterLink ?? this.backRouterLink;
    this.backLabel = data?.backLabel ?? this.backLabel;
    this.isClaimSpecific = data?.isClaimSpecific || false;

    const list = await this.api.list.get();

    this.clientOptions = list.clients;

    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      (value: any) => {
        this.onSelectClient(value);
      },
    );

    this.dispose.subs = this.fg.controls.followupUser.valueChanges.subscribe(
      (followUpUser: string) => {
        const selected = this.fg.controls.selectedUsers.value;
        if (
          followUpUser &&
          Array.isArray(selected) &&
          selected.some((id) => String(id) === String(followUpUser))
        ) {
          this.fg.controls.selectedUsers.setValue(
            selected.filter((id) => String(id) !== String(followUpUser)),
            { emitEvent: false },
          );
        }
        this.clearFollowupDuplicateError();
      },
    );
    this.dispose.subs = this.fg.controls.selectedUsers.valueChanges.subscribe(
      () => {
        this.clearFollowupDuplicateError();
      },
    );

    this.dispose.subs = this.fg.controls.dueDate.valueChanges.subscribe(
      (value) => {
        this.keepNotificationOnDueDate(value);
        void this.updateDueDateHelpText(value);
      },
    );

    this.dispose.subs = this.fg.controls.sendEmail.valueChanges.subscribe(
      (enabled) => {
        if (enabled) {
          this.ensureNotificationTimeDefaults();
        }
      },
    );

    this.setNotificationTimeFromBrowser();

    if (this.clientOptions.length === 1) {
      this.fg.controls.client.setValue(this.clientOptions[0].value);
    }

    if (data?.isClaimSpecific) {
      this.fg.controls.client.setValue(String(data.clientNumber));
      this.fg.controls.client.disable();
    }

    if (data?.id) {
      this.editMode = true;
      this.isPreloading = true;
      this.currentDiaryId = data.id;
      this.isDiaryTiedToClaim = this.checkIfDiaryTiedToClaim(data);
      const followUpUserId =
        data.followUpUser?.match(/\(([^)]+)\)/)?.[1] ??
        data.followUpUser ??
        null;
      this.headerLabel = 'Edit diary';
      const rawClientValue = data.clientId || data.clientNumber;
      const clientValue = String(rawClientValue);
      this.fg.controls.client.setValue(clientValue);

      this.fg.controls.diaryType.setValue(data.diaryTypeId);
      this.fg.controls.followupUser.setValue(followUpUserId);
      this.fg.controls.selectedUsers.setValue(
        data.additionalUsers?.map((x: any) => x.userID),
      );
      this.fg.controls.dueDate.setValue(new Date(data.dueDate));
      this.fg.controls.defaultText.setValue(data.message);
      this.fg.controls.allowOthers.setValue(data.allowOthers);
      this.fg.controls.sendEmail.setValue(data.emailReminder);
      this.fg.controls.completed.setValue(data.isCompleted);
      this.applyNotificationTimeFromDiary(data);
      if (this.isDiaryTiedToClaim) {
        this.fg.controls.client.disable();
      }
      this.allowComplete =
        data.allowOthers ||
        !!data.createdBy?.includes(`(${this.session.user.userName})`);
      this.setAuditDisplayFields(data);
    } else {
      await this.applyAllowStatusPreferenceDefault();
    }

    void this.updateDueDateHelpText(this.fg.controls.dueDate.value);
  }

  private nearestQuarterHour(minutes: number): number {
    return [0, 15, 30, 45].reduce((prev, curr) =>
      Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev,
    );
  }

  private setNotificationTimeFromBrowser(): void {
    const now = new Date();
    const nearestMinute = this.nearestQuarterHour(now.getMinutes());
    this.fg.controls.notificationTime.setValue(
      this.dateOnDueDate(
        now.getHours(),
        nearestMinute,
        this.fg.controls.dueDate.value ?? now,
      ),
      { emitEvent: false },
    );
    this.fg.controls.notifyTimezone.setValue(defaultDiaryZoneValue(now), {
      emitEvent: false,
    });
  }

  private ensureNotificationTimeDefaults(): void {
    if (!this.fg.controls.notificationTime.value) {
      this.setNotificationTimeFromBrowser();
    }
  }

  private applyNotificationTimeFromDiary(data: any): void {
    if (!data?.emailReminder) {
      return;
    }
    const dueDate = this.fg.controls.dueDate.value ?? data.dueDate;
    const hasClock = data.hours != null && data.minutes != null;
    const hasInstant =
      data.notificationTime != null && data.notificationTime !== '';
    if (!hasClock || !hasInstant) {
      this.fg.controls.notificationTime.setValue(
        this.dateOnDueDate(15, 0, dueDate),
        {
          emitEvent: false,
        },
      );
      this.fg.controls.notifyTimezone.setValue(6, { emitEvent: false });
      return;
    }
    const wall = wallClockFromCentral(
      data.notificationTime,
      data.timeOffset,
      dueDate,
    );
    this.fg.controls.notificationTime.setValue(
      this.dateOnDueDate(wall.hour24, wall.minute, dueDate),
      { emitEvent: false },
    );
    this.fg.controls.notifyTimezone.setValue(wall.zoneValue, {
      emitEvent: false,
    });
  }

  private keepNotificationOnDueDate(dueDate: Date | null): void {
    const current = this.fg.controls.notificationTime.value;
    if (!dueDate || isNaN(dueDate.getTime()) || !current) {
      return;
    }
    const next = this.dateOnDueDate(
      current.getHours(),
      current.getMinutes(),
      dueDate,
    );
    if (next.getTime() !== current.getTime()) {
      this.fg.controls.notificationTime.setValue(next, { emitEvent: false });
    }
  }

  private dateOnDueDate(
    hour24: number,
    minute: number,
    dueDate: Date | string | null | undefined,
  ): Date {
    const due =
      dueDate instanceof Date
        ? dueDate
        : typeof dueDate === 'string' && dueDate
          ? new Date(dueDate)
          : new Date();
    const base = !isNaN(due.getTime()) ? due : new Date();
    return new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      hour24,
      minute,
      0,
      0,
    );
  }

  private selectedDiaryType(): { id: string; description: string } {
    const raw = this.fg.value.diaryType as ListItem | string | number | null;
    const typeValue =
      raw != null && typeof raw === 'object' ? raw.value : raw;
    const option = this.diaryTypes.find(
      (item): boolean => String(item.value) === String(typeValue),
    );
    return {
      id: typeValue == null ? '' : String(typeValue),
      description: option?.description ?? '',
    };
  }

  private selectedFollowUpUser(): { id: string; description: string } {
    const id = this.fg.value.followupUser ?? '';
    const option = this.nameOptions.find(
      (item): boolean => String(item.value) === String(id),
    );
    return {
      id: id == null ? '' : String(id),
      description: option?.description ?? '',
    };
  }

  private searchPatchKey(path: string): string {
    const page = (path || '/todo').split('?')[0];
    return `client-diary-search-patch:${page}`;
  }

  /** New diary only: default Allow other users checkbox from user preference (3.5.003). */
  private async applyAllowStatusPreferenceDefault(): Promise<void> {
    try {
      const prefs = await this.api.preferences.getPreferences({
        type: DIARY_PREF_TYPE,
        key: DIARY_PREF_ALLOW_STATUS_UPDATE,
        value: '',
      });
      const allowStatusUpdate = readDiaryYesNoPreference(
        prefs,
        DIARY_PREF_ALLOW_STATUS_UPDATE,
        'Y',
      );
      this.fg.controls.allowOthers.setValue(allowStatusUpdate === 'Y', {
        emitEvent: false,
      });
    } catch {
      this.fg.controls.allowOthers.setValue(true, { emitEvent: false });
    }
  }

  private applyCreatedByDisplay(createdBy: string, users: any[]): void {
    const text = (createdBy ?? '').trim();
    if (!text) {
      return;
    }
    const user = (users ?? []).find((item) => {
      const description = item.description?.trim() ?? '';
      const name =
        item.lastName?.trim() && item.firstName?.trim()
          ? `${item.lastName.trim()}, ${item.firstName.trim()}`
          : '';
      return text === description || (name && text === name);
    });
    const id = String(user?.userID ?? user?.userName ?? '').trim();
    this.createdByDisplay =
      id && text !== id && !text.includes(`(${id})`) ? `${text} (${id})` : text;
  }

  private setAuditDisplayFields(data: any): void {
    this.createdByDisplay = data.createdBy ?? '';
    this.createdOnDisplay = data.dateCreated
      ? this.formatPipe.transform(data.dateCreated, 'd')
      : '';
    this.updatedByDisplay = (data.updatedBy ?? '').trim();
    this.updatedOnDisplay = data.dateUpdated
      ? this.formatPipe.transform(data.dateUpdated, 'd')
      : '';
    this.showUpdatedFields = this.hasDiaryUpdate(data);
  }

  private hasDiaryUpdate(data: any): boolean {
    if ((data.updatedBy ?? '').trim()) {
      return true;
    }
    if (!data.dateUpdated || !data.dateCreated) {
      return false;
    }
    return (
      new Date(data.dateUpdated).getTime() !==
      new Date(data.dateCreated).getTime()
    );
  }

  private async updateDueDateHelpText(dueDate: Date | null): Promise<void> {
    const generation = ++this.dueDateHelpGeneration;
    if (!dueDate || isNaN(dueDate.getTime())) {
      this.dueDateHelpText = '';
      return;
    }

    try {
      const response = await this.api.clientDiary.getDiaryCountByDate({
        dueDate,
      });
      if (generation !== this.dueDateHelpGeneration) {
        return;
      }
      const count = response.count ?? 0;
      const noun = count === 1 ? 'diary' : 'diaries';
      this.dueDateHelpText = `You have ${count} ${noun} due on this date`;
    } catch {
      if (generation !== this.dueDateHelpGeneration) {
        return;
      }
      this.dueDateHelpText = '';
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async onSelectClient(clientNumber: number) {
    const generation = ++this.generalInfoGeneration;
    this.diaryTypes = [{ description: '---', value: 'x' }];
    this.nameOptions = [{ description: '---', value: 'x' }];
    if (!this.isPreloading) {
      this.fg.controls.diaryType.setValue(null);
      this.fg.controls.followupUser.setValue(null);
      this.fg.controls.selectedUsers.setValue(null);
    }

    const data = this.storage.get('current-diary');
    const claimFields = data?.isClaimSpecific
      ? this.getClaimSpecificFields()
      : null;
    const generalInfo = await this.api.clientDiary.getGeneralInfo({
      clientNumber: clientNumber,
      claimUID: claimFields?.claimUID ?? null,
      absenceUID: claimFields?.absenceUID ?? null,
      aDAEventNumber: claimFields?.aDAEventNumber ?? null,
      leaveNumber: claimFields?.leaveNumber ?? null,
    });
    if (generation !== this.generalInfoGeneration) {
      return;
    }
    this.diaryTypes = generalInfo.diaryTypes.map((item: any) => ({
      description: item.description,
      value: item.typeUID,
    }));
    this.nameOptions = mapDiaryUserOptions(generalInfo.users);
    this.applyCreatedByDisplay(data?.createdBy, generalInfo.users);
    this.isPreloading = false;

    if (!this.editMode && !this.fg.controls.followupUser.value) {
      const currentUser = generalInfo.users.find(
        (user: any) =>
          user.userID === this.session.user.userName ||
          user.userName === this.session.user.userName,
      );
      if (currentUser?.userID) {
        this.fg.controls.followupUser.setValue(currentUser.userID);
      }
    }
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
    errorProperty: 'actionError',
  })
  async onSaveDiary() {
    const followUpUser = this.fg.value.followupUser;
    const additionalUsers = this.fg.value.selectedUsers ?? [];
    const hasDuplicate =
      followUpUser &&
      additionalUsers.some((id) => String(id) === String(followUpUser));
    if (hasDuplicate) {
      this.fg.controls.selectedUsers.setErrors({
        followupDuplicate:
          'The same user cannot be selected for both Follow-up user and Additional follow-up users.',
      });
      this.fg.controls.selectedUsers.markAsTouched();
      this.fg.markAllAsTouched();
      focusFirstInvalid(this.fg, this.formEl?.nativeElement);
      return;
    }
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      focusFirstInvalid(this.fg, this.formEl?.nativeElement);
      return;
    }

    const saveDiary = async () => {
      const data = this.storage.get('current-diary');
      const emailReminder = !!this.fg.value.sendEmail;
      const zoneValue = this.fg.value.notifyTimezone ?? 6;
      const due = this.fg.value.dueDate;
      const picked = this.fg.value.notificationTime;
      const notificationTime =
        emailReminder && due && picked
          ? new Date(
              Date.UTC(
                due.getFullYear(),
                due.getMonth(),
                due.getDate(),
                picked.getHours(),
                picked.getMinutes(),
                0,
                0,
              ),
            )
          : undefined;
      const timeOffset = emailReminder
        ? storedOffsetForZone(zoneValue, due)
        : undefined;

      const body = {
        diaryUID: this.currentDiaryId,
        followUpUser: this.fg.value.followupUser,
        dueDate: due,
        typeUID: this.fg.value.diaryType?.value ?? this.fg.value.diaryType,
        completeByCreator: !this.fg.value.allowOthers,
        emailReminder,
        notificationTime,
        timeOffset,
        description: this.fg.value.defaultText,
        tempUser: this.fg.value.tempUser,
        additionalUsers: this.fg.value.selectedUsers ?? [],
        completed: this.fg.value.completed,
        clientNumber: data?.isClaimSpecific
          ? this.view.currentClaim()?.contractNumber
          : this.fg.getRawValue().client,
        ...(data?.isClaimSpecific && {
          ...this.getClaimSpecificFields(),
        }),
        timezone: windowsZoneId(zoneValue),
      };

      let savedEntries: any[] = [];
      if (!this.editMode) {
        await this.api.clientDiary.createDiary(body);
      } else {
        const saved = await this.api.clientDiary.updateDiary(body);
        savedEntries = Array.isArray(saved) ? saved : [];
      }

      if (data?.id != null) {
        const patchKey = this.searchPatchKey(this.backRouterLink);
        const diaryType = this.selectedDiaryType();
        const followUpUser = this.selectedFollowUpUser();
        const entry = savedEntries.find(
          (item) => Number(item?.diaryUID) === Number(data.id),
        );
        const rawClient = data?.isClaimSpecific
          ? this.view.currentClaim()?.contractNumber
          : this.fg.getRawValue().client;
        const clientNumber =
          rawClient == null || rawClient === '' ? null : Number(rawClient);
        this.storage.set(patchKey, {
          id: data.id,
          message: this.fg.value.defaultText,
          dueDate: this.fg.value.dueDate,
          emailReminder,
          allowOthers: this.fg.value.allowOthers,
          isCompleted: !!this.fg.value.completed,
          typeUID: diaryType.id,
          typeDescription: diaryType.description,
          followUpUserId: followUpUser.id,
          followUpUser: followUpUser.description,
          clientNumber: Number.isFinite(clientNumber) ? clientNumber : null,
        });
        if (this.editMode) {
          rememberDiarySearchUpdate(this.storage, {
            id: data.id,
            dueDate: this.fg.value.dueDate ?? undefined,
            message: this.fg.value.defaultText ?? '',
            allowOthers: !!this.fg.value.allowOthers,
            emailReminder,
            isCompleted: !!this.fg.value.completed,
            diaryTypeId: diaryType.id,
            followUpUser: followUpUser.description,
            additionalUsers: (this.fg.value.selectedUsers ?? []).map(
              (userID: string) => ({ userID }),
            ),
            clientNumber: Number.isFinite(clientNumber) ? clientNumber : null,
          });
        }
        this.storage.set('current-diary', {
          ...data,
          message: this.fg.value.defaultText,
          dueDate: this.fg.value.dueDate,
          emailReminder,
          allowOthers: this.fg.value.allowOthers,
          isCompleted: !!this.fg.value.completed,
          typeUID: diaryType.id,
          diaryTypeId: diaryType.id,
          typeDescription: diaryType.description,
          diaryType: diaryType.description,
          followUpUserId: followUpUser.id,
          followUpUser: followUpUser.description,
          ...(entry
            ? {
                notificationTime: entry.notificationTime,
                hours: entry.hours,
                minutes: entry.minutes,
                timeOffset: entry.timeOffset,
              }
            : { timeOffset }),
          timezone: windowsZoneId(zoneValue),
        });
      }

      this.clientDiaryData.notifyChanged();
      this.router.navigate([this.backRouterLink]);
    };

    const markingComplete =
      this.editMode && this.allowComplete && !!this.fg.value.completed;

    if (markingComplete) {
      this.confirm.show({
        label: 'Mark diary complete',
        message: 'Are you sure you want to mark 1 diary complete?',
        width: '35rem',
        yesLabel: 'Yes',
        noLabel: 'No',
        confirm: async () => {
          await saveDiary();
        },
      });
      return;
    }

    await saveDiary();
  }

  private clearFollowupDuplicateError(): void {
    const followUpUser = this.fg?.controls?.followupUser?.value;
    const selected = this.fg?.controls?.selectedUsers?.value ?? [];
    const hasDuplicate =
      followUpUser &&
      selected.some((id) => String(id) === String(followUpUser));
    if (!hasDuplicate) {
      const err = this.fg?.controls?.selectedUsers?.errors;
      if (err?.['followupDuplicate']) {
        const { followupDuplicate: _, ...rest } = err;
        this.fg.controls.selectedUsers.setErrors(
          Object.keys(rest).length ? rest : null,
          { emitEvent: false },
        );
      }
    }
  }

  private checkIfDiaryTiedToClaim(data: any): boolean {
    const recordSource = data?.recordSource || data?.RecordSource;
    return !!(
      recordSource &&
      (data?.claimUID || data?.recordId) &&
      ['J', 'T', 'A', 'L', 'R'].includes(recordSource)
    );
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    if (!claim) {
      return {
        claimUID: null,
        absenceUID: null,
        aDAEventNumber: null,
        leaveNumber: null,
        recordId: null,
      };
    }

    switch (claim.recordSource) {
      case 'J':
        return {
          claimUID: claim.recordId,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'T':
        return {
          claimUID: null,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'A':
        return {
          claimUID: null,
          absenceUID: null,
          aDAEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
          leaveNumber: null,
          recordId: claim.recordId,
        };
      case 'L':
        return {
          claimUID: null,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: numericAdaOrLeaveKeyFromClaim(claim),
          recordId: claim.recordId,
        };
      default:
        return {
          claimUID: claim.recordId,
          absenceUID: null,
          aDAEventNumber: null,
          leaveNumber: null,
          recordId: claim.recordId,
        };
    }
  }
}
